import * as React from 'react';
import { ApolloQueryResult } from '@apollo/client/core/types';
import capitalize from 'lodash/capitalize';
import flatten from 'lodash/flatten';
import groupBy from 'lodash/groupBy';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import memoize from 'lodash/memoize';
import negate from 'lodash/negate';
import startCase from 'lodash/startCase';
import LoadingButton from '@atlaskit/button/loading-button';
import Lozenge from '@atlaskit/lozenge';
import ToolTip from '@atlaskit/tooltip';
import {
  /*AdaRedirectButton,*/
  analytics,
  createOpenSidebarFromButtonEvent,
} from '@atlassian/temp-analytics';
import {
  AdaRedirectButton,
  BreadcrumbItem,
  FormattedDate,
  HumanReadableIdentifierMissingIcon,
  MoreActions,
  MoreActionsItem,
  OpenSidebarButton,
  StatusLozenge,
  hasAtleastOneRole,
  isAccess,
  isResourceAccess,
  useSidebar,
} from '@css-xp/base';
// @ts-ignore
import InvoiceImage from '@css-xp/base/assets/img/icon-invoice.svg';
import { InvoiceStatus } from '@css-xp/graphtypes';
import { downloadInvoicePdf } from '@atlassian/temp-proxies';
import {
  BillingDoc,
  BillingDocWithLineItems,
  CreditNotetype,
  Invoice,
  InvoiceLineItem,
  InvoiceWithLineItems,
  PaymentMethods,
} from '@atlassian/cssxp-types-billing';
import { Organization } from '@atlassian/cssxp-types-organization';
import {
  DOMAIN_PATH,
  INVOICE_PATH,
  ORGANIZATION_PATH,
  Permissions,
  Resources,
  Roles,
  SourceSystem,
} from '@atlassian/cssxp-typescommon';
import { GloballyAllowedSidebar } from '@atlassian/cssxp-utils-generic';
import {
  isInvoiceCreditable,
  isInvoiceRefundable,
  isTaxRefundable,
  isUncollectible,
} from '@atlassian/cssxp-utils-invoice';
import { isCloudParent, isOrganisationParent, isStride } from '@atlassian/cssxp-utils-product';

interface PdfRedirectProps {
  href: string;
  label?: string;
  transactionAccountId?: string | null | undefined;
  invoiceId?: string;
}

const getViewInAdaInvoiceUrl = memoize(
  (baseUrl?: string, id?: string) => {
    if (id && baseUrl) {
      const idNumber = id.startsWith('AT-') ? id.slice(3) : id;
      return `${baseUrl}/ViewOrder.jspa?orderId=${idNumber}`;
    }
    return '';
  },
  (...arg) => arg[1]
);

const partnerLozenge = (partnerType: string): JSX.Element => (
  <StatusLozenge status={'whitelisted'} label={`${partnerType.toUpperCase()} PARTNER`} />
);

const getInvoiceHeader = (billingDoc: BillingDoc): React.ReactNode => {
  const { invoiceNumber, id, sourceSystem, partnerLevel } = billingDoc as Invoice;
  let headerValue;
  if (sourceSystem === SourceSystem.CCP) {
    headerValue = (
      <>
        {invoiceNumber ? (
          invoiceNumber
        ) : (
          <>
            {id} <HumanReadableIdentifierMissingIcon />
          </>
        )}
      </>
    );

    return (
      <>
        {headerValue}
        {partnerLevel && partnerLevel.length > 0 && partnerLozenge(partnerLevel)}
      </>
    );
  }

  return <>{id}</>;
};

const getInvoiceSubheader = (billingDoc: BillingDoc): React.ReactNode => {
  const { status } = billingDoc as Invoice;
  const { refunds } = billingDoc as Invoice;
  const refundMessage = 'One or more credit notes have been issued for this invoice';

  return (
    <div>
      {status && (
        <Lozenge appearance={status === 'PAID' ? 'success' : 'inprogress'}>
          {capitalize(startCase(status ?? ''))}
        </Lozenge>
      )}

      {refunds.length > 0 && (
        <>
          &nbsp;
          <ToolTip content={refundMessage} tag="span" position="mouse">
            <Lozenge isBold={false} appearance={'removed'}>
              <span>{'REFUNDED'} </span>
            </Lozenge>
          </ToolTip>
        </>
      )}
    </div>
  );
};

const getInvoiceIcon = (): JSX.Element => {
  return (
    // @ts-ignore
    <img src={InvoiceImage} height={'60px'} alt="invoice" />
  );
};

export const InvoiceMoreActions = ({
  invoice,
  onComplete,
}: {
  invoice: InvoiceWithLineItems;
  onComplete: () => Promise<ApolloQueryResult<unknown>>;
}) => {
  const { openSidebar, isSidebarOpen } = useSidebar();
  const invoiceActions: MoreActionsItem[] = [
    {
      id: 'create-service-credit',
      content: 'Request credit',
      predicate:
        invoice.sourceSystem === SourceSystem.HAMS &&
        isResourceAccess(Resources.INVOICE_ALL) &&
        isAccess(Permissions.INVOICE_CREDIT_MEMO_WRITE) &&
        hasAtleastOneRole([Roles.COGS_BITBUCKET_CLOUD_SUPPORT, Roles.COGS_CLOUD_SUPPORT]),
      isDisabled: !isInvoiceCreditable(invoice),
      action: () => {
        analytics.sendUIEvent(createOpenSidebarFromButtonEvent(GloballyAllowedSidebar.BILLING_SERVICE_CREDIT_FORM));
        openSidebar({
          sidebarType: GloballyAllowedSidebar.BILLING_SERVICE_CREDIT_FORM,
          payload: {
            invoiceId: invoice.id,
          },
        });
      },
    },
    {
      id: 'create-refund',
      content: 'Process refund',
      predicate:
        invoice.sourceSystem === SourceSystem.HAMS &&
        isResourceAccess(Resources.INVOICE_ALL) &&
        isResourceAccess(Resources.TRANSACTION_ACCOUNT_ALL) &&
        isAccess(Permissions.INVOICE_CREDIT_MEMO_WRITE) &&
        isAccess(Permissions.BILLING_ADMIN_MANAGE) &&
        hasAtleastOneRole([Roles.MKTG_GTM_SUPPORT]),
      isDisabled: !isInvoiceRefundable(invoice),
      action: () => {
        analytics.sendUIEvent(createOpenSidebarFromButtonEvent(GloballyAllowedSidebar.BILLING_PROCESS_REFUND_FORM));
        openSidebar({
          sidebarType: GloballyAllowedSidebar.BILLING_PROCESS_REFUND_FORM,
          payload: {
            invoiceId: invoice.id,
          },
        });
      },
    },
    {
      id: 'ccp-full-refund',
      content: 'Refund full invoice',
      predicate:
        invoice.sourceSystem === SourceSystem.CCP &&
        isResourceAccess(Resources.INVOICE_ALL) &&
        isResourceAccess(Resources.TRANSACTION_ACCOUNT_ALL) &&
        isAccess(Permissions.INVOICE_CREDIT_MEMO_WRITE) &&
        isAccess(Permissions.BILLING_ADMIN_MANAGE),
      isDisabled: !isInvoiceRefundable(invoice),
      action: () => {
        analytics.sendUIEvent(createOpenSidebarFromButtonEvent(GloballyAllowedSidebar.BILLING_PROCESS_REFUND_FORM_CCP));
        openSidebar({
          sidebarType: GloballyAllowedSidebar.BILLING_PROCESS_REFUND_FORM_CCP,
          payload: {
            invoiceId: invoice.id,
            invoiceNumber: invoice.invoiceNumber,
            transactionAccountId: invoice.transactionAccountId,
            refundType: CreditNotetype.FULL_ITEM_REFUND,
            lineItems: invoice.lineItems,
            paymentMethod: invoice.paymentMethod,
            invoiceStatus: invoice.status,
          },
        });
      },
    },
    {
      id: 'ccp-tax-refund',
      content: 'Refund tax',
      predicate:
        invoice.sourceSystem === SourceSystem.CCP &&
        isResourceAccess(Resources.INVOICE_ALL) &&
        isResourceAccess(Resources.TRANSACTION_ACCOUNT_ALL) &&
        isAccess(Permissions.INVOICE_CREDIT_MEMO_WRITE) &&
        isAccess(Permissions.BILLING_ADMIN_MANAGE),
      isDisabled: isTaxRefundable(invoice) || !isInvoiceRefundable(invoice),
      action: () => {
        analytics.sendUIEvent(createOpenSidebarFromButtonEvent(GloballyAllowedSidebar.BILLING_PROCESS_REFUND_FORM_CCP));
        openSidebar({
          sidebarType: GloballyAllowedSidebar.BILLING_PROCESS_REFUND_FORM_CCP,
          payload: {
            invoiceId: invoice.id,
            invoiceNumber: invoice.invoiceNumber,
            transactionAccountId: invoice.transactionAccountId,
            refundType: CreditNotetype.TAX_ONLY_REFUND,
            paymentMethod: invoice.paymentMethod,
          },
        });
      },
    },
    {
      id: 'ccp-line-items-refund',
      content: 'Refund line items',
      predicate:
        invoice.sourceSystem === SourceSystem.CCP &&
        isResourceAccess(Resources.INVOICE_ALL) &&
        isResourceAccess(Resources.TRANSACTION_ACCOUNT_ALL) &&
        isAccess(Permissions.INVOICE_CREDIT_MEMO_WRITE) &&
        isAccess(Permissions.BILLING_ADMIN_MANAGE),
      isDisabled: !isInvoiceRefundable(invoice),
      action: () => {
        analytics.sendUIEvent(createOpenSidebarFromButtonEvent(GloballyAllowedSidebar.BILLING_PROCESS_REFUND_FORM_CCP));
        openSidebar({
          sidebarType: GloballyAllowedSidebar.BILLING_PROCESS_REFUND_FORM_CCP,
          payload: {
            invoiceId: invoice.id,
            invoiceNumber: invoice.invoiceNumber,
            transactionAccountId: invoice.transactionAccountId,
            refundType: CreditNotetype.FULL_ITEM_REFUND,
            isRefundLineItems: true,
            lineItems: invoice.lineItems,
            paymentMethod: invoice.paymentMethod,
          },
        });
      },
    },
    {
      id: 'update-memo',
      content: 'Resend invoice with memo',
      predicate: invoice.sourceSystem === SourceSystem.CCP && isAccess(Permissions.DEAL_DESK),
      isDisabled: false,
      action: () => {
        openSidebar({
          sidebarType: 'update-memo-pane',
          payload: {
            billingDoc: invoice,
            refetch: onComplete,
          },
        });
      },
    },
    {
      id: 'resend-invoice',
      content: 'Resend invoice',
      predicate: invoice.sourceSystem === SourceSystem.CCP && isAccess(Permissions.DEAL_DESK),
      isDisabled: false,
      action: () => {
        openSidebar({
          sidebarType: 'resend-invoice-pane',
          payload: {
            withInputMemoFieldFlag: false,
            billingDoc: invoice,
            refetch: onComplete,
          },
        });
      },
    },
    {
      id: 'uncollectible-invoice',
      content: 'Mark uncollectible',
      predicate:
        invoice.sourceSystem === SourceSystem.CCP &&
        isResourceAccess(Resources.INVOICE_ALL) &&
        isAccess(Permissions.INVOICE_MANAGE),
      isDisabled: !isUncollectible(invoice),
      action: () => {
        openSidebar({
          sidebarType: 'uncollectible-pane',
          payload: {
            billingDoc: invoice,
            refetch: onComplete,
          },
        });
      },
    },
  ];

  return !isSidebarOpen ? <MoreActions actions={invoiceActions} /> : null;
};

const getInvoiceActions = (
  billingDoc: BillingDoc,
  refetch: () => Promise<ApolloQueryResult<unknown>>,
  adaBaseUrl: string,
  hidePdfLinkBtn?: boolean
) => {
  const invoice = billingDoc as InvoiceWithLineItems;
  const isPaidInvoice = invoice.status === InvoiceStatus.PAID || invoice.status === InvoiceStatus.PAYMENT_DEFERRED;

  return (
    <>
      {
        <>
          {invoice.sourceSystem === SourceSystem.HAMS && (
            <>
              <AdaRedirectButton href={getViewInAdaInvoiceUrl(adaBaseUrl, billingDoc.id)} />
              {!hidePdfLinkBtn && (
                <OpenSidebarButton sidebar={'pdf-link-pane'} payload={billingDoc} label="Get PDF link">
                  Get PDF link
                </OpenSidebarButton>
              )}
            </>
          )}
        </>
      }
      {invoice.sourceSystem === SourceSystem.CCP && isPaidInvoice && invoice.pdfLink ? (
        <PdfRedirectButton
          href={invoice.pdfLink}
          label="View PDF"
          invoiceId={invoice.id}
          transactionAccountId={invoice.transactionAccountId}
        />
      ) : null}
      <InvoiceMoreActions invoice={invoice} onComplete={refetch} />
    </>
  );
};

const getPaidLabel = (billingDoc: Invoice) => {
  if (
    billingDoc.paymentMethod &&
    // leaving as array in case more items needed to be added
    ([
      'DEFERRED',
      'PAID_BANK',
      'PAIDCC',
      'PAIDPAYPAL',
      'PAIDONACCOUNT',
      'PAID_ON_TERMS',
      'STRIPE_CARD',
      'PAID',
      'NOPAYMENT',
    ] as PaymentMethods[]).includes(billingDoc.paymentMethod)
  ) {
    if (billingDoc.status && ['PAID'].includes(billingDoc.status)) {
      return 'Paid';
    }
    return 'Due by';
  }
  return 'Due by';
};

const getInvoiceAdditionalDetails = (billingDoc?: BillingDocWithLineItems) => {
  if (!billingDoc) {
    return null;
  }

  const invoice = billingDoc as Invoice;
  return (
    <div className="billing-doc-dates">
      <h4>
        Issued: <FormattedDate date={invoice.createdTimestamp} />
      </h4>
      <h4>
        {getPaidLabel(billingDoc as Invoice)}:{' '}
        <span>
          <FormattedDate date={invoice.dueDate} />
        </span>
      </h4>
      {/*<FeatureSwitch flag={ENABLE_PO_NUMBER_INVOICE_GROUP}>
            <Enabled> */}
      {invoice.sourceSystem === SourceSystem.CCP && (
        <h4>PO Number: {!isNil(invoice.purchaseOrderNumber) ? invoice?.purchaseOrderNumber : 'N/A'}</h4>
      )}
      {/*</div></Enabled>
        </FeatureSwitch> */}
    </div>
  );
};

/*
 * For Atlassian Cloud, find all linked line items and nest underneath
 * For Atlassian Org/AA, leave Identity Manager and filter out Atlassian Org line item
 * For Stride, nest under Atlassian Cloud if there is only one cloud instance on invoice; otherwise make it standalone
 * Otherwise, standalone line item
 */
const getCloudProductLineItemsByCloudUrl = (lineItems: InvoiceLineItem[]): { [key: string]: InvoiceLineItem[] } => {
  const cloudContainerLineItems = lineItems.filter((item) => item.productKey && isCloudParent(item.productKey));

  const cloudSiteHostnames = cloudContainerLineItems
    .map((item) => item.cloudSiteHostname)
    .filter(negate(isNil)) as string[];

  if (cloudSiteHostnames.length === 0) {
    return {};
  }

  const cloudContainerProducts = lineItems.filter(
    (item) =>
      item.cloudSiteHostname &&
      !cloudContainerLineItems.includes(item) &&
      cloudSiteHostnames.includes(item.cloudSiteHostname)
  );

  const strideLineItems = lineItems.filter((item) => item.productKey && isStride(item.productKey));

  if (cloudSiteHostnames.length === 1) {
    return {
      [cloudSiteHostnames[0]]: [...cloudContainerProducts, ...strideLineItems],
    };
  }

  return groupBy(cloudContainerProducts, (product) => product.cloudSiteHostname);
};

/**
 * Separating if needed; customer invoice does not map child / parent relationships
 */
const formatInvoiceLineItemsCCP = (invoiceLineItems: InvoiceLineItem[]) => {
  return invoiceLineItems;
};

const formatInvoiceLineItems = (invoiceLineItems: InvoiceLineItem[]) => {
  const lineItems = invoiceLineItems.filter((item) => item.productKey && !isOrganisationParent(item.productKey));

  const cloudContainerProductMap = getCloudProductLineItemsByCloudUrl(lineItems);

  const cloudContainerProductLineItems = flatten(Object.values(cloudContainerProductMap));

  const topLevelLineItems = lineItems.filter((item) => !cloudContainerProductLineItems.includes(item));

  return topLevelLineItems.map((item) => {
    if (!item.cloudSiteHostname || !cloudContainerProductMap[item.cloudSiteHostname]) {
      return {
        ...item,
        products: [],
      };
    }

    return {
      ...item,
      products: cloudContainerProductMap[item.cloudSiteHostname],
    };
  });
};

const getInvoiceBreadcrumbTrail = (billingDoc: BillingDoc, organization?: Organization) => {
  const { id, invoiceNumber, domain, sourceSystem } = billingDoc as Invoice;
  const breadcrumbTrail: BreadcrumbItem.BreadcrumbItem[] = [];

  // if CCP, prefer Invoice Number (human readable), else fall back to id
  const identifier = sourceSystem === SourceSystem.CCP ? invoiceNumber : id;

  breadcrumbTrail.unshift({
    title: identifier ?? id,
    href: `${INVOICE_PATH}/${encodeURIComponent(identifier ?? id)}`,
  });

  if (domain && isValidBreadCrumb(domain)) {
    breadcrumbTrail.unshift({
      title: domain,
      href: `${DOMAIN_PATH}/${encodeURIComponent(domain)}`,
    });
  }

  if (organization && isValidBreadCrumb(organization?.organizationName)) {
    breadcrumbTrail.unshift({
      title: `${organization.organizationName}`,
      href: `${ORGANIZATION_PATH}/${encodeURIComponent(`${organization.organizationId}`)}`,
    });
  }

  return breadcrumbTrail;
};

const isValidBreadCrumb = (token: string | undefined) => {
  return token && !isEmpty(token) && token !== 'undefined';
};

const PdfRedirectButton = ({ href, label, transactionAccountId, invoiceId }: PdfRedirectProps) => {
  const [pdfLink, setPdfLink] = React.useState(href);
  const [isLoading, setIsLoading] = React.useState(true);
  React.useEffect(() => {
    getCCPInvoicePdfUrl();
  }, []);
  const getCCPInvoicePdfUrl = async () => {
    if (invoiceId && transactionAccountId) {
      const pdfFileUrl = await downloadInvoicePdf(invoiceId, transactionAccountId);
      const fileUrl = pdfFileUrl ?? href;

      setPdfLink(fileUrl);
      setIsLoading(false);
    } else {
      setIsLoading(false);
    }
  };

  return (
    <LoadingButton appearance="default" href={pdfLink} target="_blank" isLoading={isLoading} testId="pdf-link-btn">
      {label || `View PDF`}
    </LoadingButton>
  );
};
export {
  getViewInAdaInvoiceUrl,
  formatInvoiceLineItems,
  formatInvoiceLineItemsCCP,
  getInvoiceActions,
  getInvoiceAdditionalDetails,
  getInvoiceIcon,
  getInvoiceHeader,
  getInvoiceSubheader,
  getInvoiceBreadcrumbTrail,
  PdfRedirectButton,
};
